import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { redeemShareLink } from "@/sharing/shareTokenClient";
import { Loader2, AlertCircle, Check } from "lucide-react";
import { NuvraLogo } from "@/components/NuvraLogo";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Redeem a project share link (Phase 6, section 3).
 *
 * The emailed/shared link points here with ?token=... . Redemption requires a
 * signed-in user, so an unauthenticated visitor is sent to /login with a return
 * path back here (the token stays in the URL). On success the user is added as a
 * project member at the token's role and routed into the project.
 */
export default function JoinPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [state, setState] = useState<"working" | "done" | "error">("working");
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  const token = params.get("token") ?? "";

  useEffect(() => {
    if (loading) return;

    if (!token) {
      setState("error");
      setMessage(t("join.missingToken"));
      return;
    }

    // Redemption needs an account. Bounce to login, preserving the return path.
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
      return;
    }

    if (ran.current) return;
    ran.current = true;

    void (async () => {
      const result = await redeemShareLink(token);
      if (result.ok && result.projectId) {
        setState("done");
        setMessage(t("join.accessGranted", { role: result.role ?? "member" }));
        setTimeout(() => navigate(`/projects/${result.projectId}`, { replace: true }), 900);
      } else {
        setState("error");
        setMessage(result.error ?? t("join.invalid"));
      }
    })();
  }, [loading, user, token, navigate, location, t]);

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="text-center space-y-4 max-w-sm px-4">
        <div className="flex items-center justify-center gap-2">
          <NuvraLogo size={28} />
          <span className="font-bold text-lg text-foreground">Nuvra</span>
        </div>
        {state === "working" && (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
            <p className="text-sm text-muted-foreground">{t("join.joining")}</p>
          </>
        )}
        {state === "done" && (
          <>
            <Check className="h-8 w-8 text-primary mx-auto" />
            <p className="text-sm text-muted-foreground">{message}</p>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
            <p className="text-sm text-muted-foreground">{message}</p>
            <Button onClick={() => navigate("/dashboard", { replace: true })}>
              {t("join.goToDashboard")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
